import { Eye, EyeOff } from "lucide-react";
import { useId, useState, type InputHTMLAttributes } from "react";

/**
 * Il campo della password, con l'occhio che mostra quello che si sta scrivendo.
 *
 * Serve a chi digita e serve a chi consegna: una password temporanea si legge al telefono, e
 * senza vederla si sbaglia una lettera e si dice che il programma non funziona. Il campo nasce
 * sempre **nascosto**, perche il caso normale e uno che entra davanti a qualcuno.
 *
 * Vive in un posto solo, come le unita di misura in `material-units.ts`: accesso, cambio
 * password, invito e console usano questo, cosi l'occhio non compare in tre schermate su
 * cinque. Il pulsante non si spegne mai e dichiara a parole cosa sta per fare.
 *
 * Funziona sia col campo governato da React (`value` piu `onChange`) sia con quello letto al
 * momento dell'invio (`name` piu `FormData`): non decide lui come si legge il valore.
 */
export function PasswordField({
  label,
  className,
  ...input
}: { label: string } & InputHTMLAttributes<HTMLInputElement>) {
  const [visibile, setVisibile] = useState(false);
  const id = useId();
  // L'etichetta punta al campo con `htmlFor` e non lo avvolge: dentro c'e anche l'occhio, e
  // una etichetta che avvolge due comandi finisce per dare lo stesso nome a tutti e due.
  return (
    <div className={className ? `password-field ${className}` : "password-field"}>
      <label htmlFor={id}>{label}</label>
      <span className="password-field__control">
        <input {...input} id={id} type={visibile ? "text" : "password"} />
        <button
          type="button"
          className="password-field__eye"
          aria-label={visibile ? "Nascondi la password" : "Mostra la password"}
          aria-pressed={visibile}
          onClick={() => setVisibile((prima) => !prima)}
        >
          {visibile ? <EyeOff size={18} aria-hidden="true" /> : <Eye size={18} aria-hidden="true" />}
        </button>
      </span>
    </div>
  );
}
